import { useCallback, useEffect, useState } from "react";
import { subscribeToast, type ToastItem, type ToastVariant } from "@client/components/toast";

const VARIANT_CLASS: Record<ToastVariant, string> = {
  success: "alert-success",
  error: "alert-error",
  info: "alert-info",
};

const VARIANT_GLYPH: Record<ToastVariant, string> = {
  success: "✓",
  error: "!",
  info: "i",
};

function ToastCard({ item, onClose }: { item: ToastItem; onClose: (id: string) => void }) {
  const [leaving, setLeaving] = useState(false);

  const close = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onClose(item.id), 220);
  }, [item.id, onClose]);

  useEffect(() => {
    const hide = setTimeout(() => setLeaving(true), 3_600);
    const remove = setTimeout(() => onClose(item.id), 4_150);
    return () => {
      clearTimeout(hide);
      clearTimeout(remove);
    };
  }, [item.id, onClose]);

  return (
    <div
      className={`alert ${VARIANT_CLASS[item.variant]} pointer-events-auto items-start gap-3 shadow-xl ${
        leaving ? "animate-toast-out" : "animate-toast-in"
      }`}
    >
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-black/20 text-xs font-bold">
        {VARIANT_GLYPH[item.variant]}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{item.title}</p>
        {item.description ? (
          <p className="mt-0.5 text-xs break-words opacity-80">{item.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Cerrar"
        className="btn btn-ghost btn-xs shrink-0"
      >
        ✕
      </button>
    </div>
  );
}

// Contenedor fijo de notificaciones (esquina inferior).
export function Toaster() {
  const [items, setItems] = useState<ToastItem[]>([]);

  useEffect(() => subscribeToast((item) => setItems((prev) => [...prev, item].slice(-4))), []);

  const remove = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-stretch gap-2 sm:inset-x-auto sm:right-4 sm:w-[22rem] sm:items-end">
      {items.map((item) => (
        <ToastCard key={item.id} item={item} onClose={remove} />
      ))}
    </div>
  );
}
